import { useState, useRef } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'

export default function DragDropFileUpload({
    acceptedFileType = '.csv',
    maxSizeMB = 5,
    fileData = null,
    setFileData,
    setErrorText
}) {

    const fileInputRef = useRef(null)

    const [isDragging, setIsDragging] = useState(false)

    const HandleDragOver = (e) => {
        e.preventDefault()
        setIsDragging(true)
    }

    const HandleDragLeave = (e) => {
        e.preventDefault()
        setIsDragging(false)
    }

    const HandleDrop = (e) => {
        e.preventDefault()
        setIsDragging(false)

        const files = e.dataTransfer.files

        if (files.length > 0) {
            ProcessFile(files[0])
        }
    }

    const HandleFileInput = (e) => {
        const files = e.target.files

        if (files.length > 0) {
            ProcessFile(files[0])
        }
    }

    const ProcessFile = async (file) => {
        
        try {
            setErrorText('')
        
            // Check file type

            const validExtensions = acceptedFileType.split(',').map(ext => ext.trim().replace('.', ''))
            const fileExtension = file.name.split('.').pop().toLowerCase()
            
            if (!validExtensions.includes(fileExtension)) {
                setErrorText(`Invalid file type. Please upload a ${acceptedFileType} file.`)
                return
            }

            // Check file size

            const maxSizeBytes = maxSizeMB * 1024 * 1024
            
            if (file.size > maxSizeBytes) {
                setErrorText(`File size exceeds ${maxSizeMB}MB limit.`)
                return
            }
            
            setFileData(file)

        } catch (err) {
            setFileData(null)

            setErrorText(err.message)
        }
    }

    // Upload area
    if (fileData === null) {
        return (
            <div
                onDragOver={HandleDragOver}
                onDragLeave={HandleDragLeave}
                onDrop={HandleDrop}
                className={`
                    border-2 border-dashed rounded-lg py-4 text-center transition-colors 
                    ${isDragging ? 'border-orange-500 bg-orange-50' : ''}
                    ${!isDragging ? 'border-gray-300 bg-gray-50 hover:border-gray-400' : ''}
                `}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptedFileType}
                    onChange={HandleFileInput}
                    className="hidden"
                />

                <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                        Drag and drop your {acceptedFileType.replace('.', '')} file here, or
                    </p>

                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center gap-x-2 px-4 py-2 text-sm font-medium text-orange-600 border border-orange-600 rounded-md hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                    >
                        Browse Files
                    </button>
                </div>

                <p className="mt-2 text-xs text-gray-500">
                    Maximum file size: {maxSizeMB}MB
                </p>
            </div>
        )
    }

    // File preview area
    if (fileData !== null) {
        return (
            <div className='border-2 border-dashed rounded-lg p-4 text-center transition-colors border-gray-300 bg-gray-50 hover:border-gray-400'>

                <div className='flex items-center'>
                    <p className='flex-grow'>{fileData.name}</p>
                    <XMarkIcon className='h-5 w-5 text-gray-400 cursor-pointer' onClick={() => setFileData(null)} />
                </div>
            </div>
        )
    }
}